import {
  CommonActions,
  NavigationProp,
  useNavigation,
} from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import { useSetAtom } from "jotai";
import { Platform } from "react-native";

import { resetMixpanelSession } from "@/utils/Analytics/Mixpanel/resetMixpanelSession/resetMixpanelSession";
import { setAnalyticsTrackingAllowed } from "@/utils/Analytics/analyticsTrackingConsent";
import { resumeNativeSessionPersistence } from "@whoosh/relay/src";
import { deleteExpoSecureStoreItemAsync } from "@whoosh/utilities/src/ExpoSecureStore/expoSecureStore";

import { BaseNavigatorParamList } from "../../../navigators/BaseNavigator/types";
import { clearPersistedNativeSession } from "../../BiometricAuthentication/biometricUnlock";
import { LANGUAGE_PREFERENCE_STORAGE_KEY } from "../../LanguagePreference/atoms";
import {
  sessionAtom,
  sessionCurrentClubAtom,
  sessionQueryVersionAtom,
} from "../../Session";
import {
  isLocallySignedOutAtom,
  isUserAuthenticated_atom,
  preAuthenticatedPath_atom,
} from "../atoms";

export const useHandleLogOutCleanUp = () => {
  const setIsAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const setIsLocallySignedOut = useSetAtom(isLocallySignedOutAtom);
  const setPreAuthenticatedPath = useSetAtom(preAuthenticatedPath_atom);
  const setSession = useSetAtom(sessionAtom);
  const incrementSessionQueryVersion = useSetAtom(sessionQueryVersionAtom);
  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();
  const setSessionCurrentClub = useSetAtom(sessionCurrentClubAtom);

  const handleLogOutCleanUp = async ({
    skipClearPreAuthPath = false,
  }: {
    skipClearPreAuthPath?: boolean;
  } = {}) => {
    setAnalyticsTrackingAllowed(false);
    setIsLocallySignedOut(true);

    if (Platform.OS !== "web") {
      await clearPersistedNativeSession();
      resumeNativeSessionPersistence();
      await deleteExpoSecureStoreItemAsync("lastSelectedFacilityId");
      Sentry.setUser(null);
      setIsAuthenticated(false);
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: "Unauthenticated" }],
        })
      );
    } else if (Platform.OS === "web") {
      const languagePreference = localStorage.getItem(
        LANGUAGE_PREFERENCE_STORAGE_KEY
      );
      localStorage.clear();
      if (languagePreference !== null) {
        localStorage.setItem(
          LANGUAGE_PREFERENCE_STORAGE_KEY,
          languagePreference
        );
      }
      Sentry.setUser(null);
      setIsAuthenticated(false);
      resetMixpanelSession();
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: "Unauthenticated" }],
        })
      );
    }
    if (!skipClearPreAuthPath) {
      setPreAuthenticatedPath(null);
    }
    incrementSessionQueryVersion((current) => current + 1);
    setSession(null);
    setSessionCurrentClub(null);
  };

  return handleLogOutCleanUp;
};
